import {
  CurrentUserTeacherUnreadCountsQueryQuery,
  CurrentUserTeacherUnreadCountsQueryQueryVariables,
  CurrentUserUnreadCountsQueryQuery,
} from "@outschool/gql-frontend-generated";
import {
  DocumentNode,
  useApolloClient,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import {
  currentUserTeacherUnreadCountsQuery,
  currentUserUnreadCountsQuery,
  useSession,
} from "@outschool/ui-auth";
import React from "react";
import { useLocation } from "react-router";

import * as User from "../../shared/User";
import { adultConversationUserModeForPath } from "../components/conversations/conversationRouteHelpers";
import { UserMode, useUserMode } from "../lib/UserMode";
import { useHybridClassroom } from "./useHybridClassroom";
import { useTeacherUnifiedMessaging } from "./useTeacherUnifiedMessaging";

export type CurrentUserUnreadCounts = {
  uid: string;
  unreadMessageThreadsCount?: number;
  unreadPrivateClassMessageThreadsCount?: number;
  unreadMessageThreadGroupsCount?: number;
  unreadAssignmentCommentThreadsCount?: number;
  unreadPublicClassPostCommentThreadsCount?: number;
};

const UNREAD_COUNTS_FRESHNESS_WINDOW_MS = 30 * 1000;
const INBOX_UNREAD_COUNTS_POLL_INTERVAL_MS = 5 * 60 * 1000;

// Shared across every mounted badge so a mount or focus event triggers one
// network fetch, not one per badge.
let lastNetworkFetchAt = 0;

function useTeacherInboxUnreadCounts() {
  const { isLeader } = useSession();
  const userMode = useUserMode();
  const { pathname } = useLocation();
  const isTeacherUnifiedMessagingEnabled = useTeacherUnifiedMessaging();
  const inboxUserMode = adultConversationUserModeForPath(pathname, userMode);
  return Boolean(
    isLeader &&
      inboxUserMode === UserMode.Educator &&
      isTeacherUnifiedMessagingEnabled,
  );
}

type UnreadCountsQueryOptions = {
  query: DocumentNode;
  variables?: CurrentUserTeacherUnreadCountsQueryQueryVariables;
};

/** The unread counts query and variables for the current inbox mode. */
export function useCurrentUserUnreadCountsQuery(): UnreadCountsQueryOptions {
  const useTeacherCounts = useTeacherInboxUnreadCounts();
  const includePublicClassPostCommentThreads = useHybridClassroom();
  return React.useMemo(
    () =>
      useTeacherCounts
        ? {
            query: currentUserTeacherUnreadCountsQuery,
            variables: { includePublicClassPostCommentThreads },
          }
        : { query: currentUserUnreadCountsQuery },
    [useTeacherCounts, includePublicClassPostCommentThreads],
  );
}

/**
 * Inbox list queries do not carry unread counts, so actions that change
 * unread state must refresh the counts themselves.
 */
export function useRefetchCurrentUserUnreadCounts() {
  const apolloClient = useApolloClient();
  const unreadCountsQuery = useCurrentUserUnreadCountsQuery();
  return React.useCallback(async () => {
    await apolloClient.query({
      ...unreadCountsQuery,
      fetchPolicy: "network-only",
    });
  }, [apolloClient, unreadCountsQuery]);
}

/**
 * Header and inbox badges all read this one query. Each refresh is throttled
 * so stacked badges, focus, and visibility changes share a single request.
 * Pass `pollWhileVisible` from the inbox so its badges keep up while open.
 */
export function useCurrentUserUnreadCounts({
  pollWhileVisible = false,
}: { pollWhileVisible?: boolean } = {}): CurrentUserUnreadCounts | null {
  const { isLoggedIn } = useSession();
  const { query, variables } = useCurrentUserUnreadCountsQuery();
  const skip = !isLoggedIn;
  const { data, refetch, startPolling, stopPolling } = useQueryWithPreviousData<
    CurrentUserTeacherUnreadCountsQueryQuery | CurrentUserUnreadCountsQueryQuery
  >(query, {
    skip,
    variables,
    fetchPolicy: "cache-first",
  });

  React.useEffect(() => {
    if (skip || typeof document === "undefined") {
      return;
    }

    const refreshIfStale = () => {
      if (document.visibilityState === "hidden") {
        return;
      }
      const now = Date.now();
      if (now - lastNetworkFetchAt < UNREAD_COUNTS_FRESHNESS_WINDOW_MS) {
        return;
      }
      lastNetworkFetchAt = now;
      refetch().catch(() => undefined);
    };
    const syncPolling = () => {
      if (!pollWhileVisible) {
        return;
      }
      if (document.visibilityState === "hidden") {
        stopPolling();
      } else {
        startPolling(INBOX_UNREAD_COUNTS_POLL_INTERVAL_MS);
      }
    };
    const handleVisibilityChange = () => {
      syncPolling();
      refreshIfStale();
    };

    refreshIfStale();
    syncPolling();
    window.addEventListener("focus", refreshIfStale);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", refreshIfStale);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (pollWhileVisible) {
        stopPolling();
      }
    };
  }, [pollWhileVisible, refetch, skip, startPolling, stopPolling]);

  return data?.currentUser ?? null;
}

export function useUnreadConversationsBadgeCount() {
  const useTeacherCounts = useTeacherInboxUnreadCounts();
  const unreadUser = useCurrentUserUnreadCounts();
  return User.unreadConversationsCount(unreadUser, {
    includeAssignmentCommentThreads: useTeacherCounts,
    unreadPublicClassPostCommentThreadsCount: useTeacherCounts
      ? unreadUser?.unreadPublicClassPostCommentThreadsCount ?? 0
      : 0,
    useMessageThreadGroupsCount: useTeacherCounts,
  });
}
